import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import '@/screens/Modals/HearthstoneRevealModal.css'
import { RevealItem } from '@/screens/Cards.tsx'

type RevealStep =
  | 'initial'
  | 'loading'
  | 'fragment'
  | 'newCard'
  | 'newUnrevealedCard'
  | 'tokens'

interface HearthstoneRevealModalProps {
  isOpen: boolean
  onClose: () => void
  initialCardUrl: string
  onReveal: () => Promise<void>
  revealedItems: RevealItem[]
  isRevealing: boolean
  tokenReward?: number
}

const loadingMessages = [
  'Opening Card...',
  'Channeling Magic...',
  'Revealing Power...',
  'Almost There...',
]

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
}

const cardVariants = {
  initial: { scale: 1, rotateY: 0 },
  flipped: { scale: 1.2, rotateY: 180 },
  exit: { scale: 0, opacity: 0 },
}

// Enhanced content variants with transitions
const contentVariants = {
  enter: {
    opacity: 0,
    scale: 0.8,
    y: 20,
  },
  center: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0.0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    y: -20,
    transition: {
      duration: 0.3,
      ease: [0.4, 0.0, 0.2, 1],
    },
  },
}

// Animation variants for the card content
const cardContentVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.8,
    transition: {
      duration: 0.3,
    },
  },
}

// Text animation variants
const textVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.3,
      duration: 0.4,
    },
  },
}

export const HearthstoneRevealModal: React.FC<HearthstoneRevealModalProps> = ({
  isOpen,
  onClose,
  initialCardUrl,
  onReveal,
  revealedItems,
  isRevealing,
  tokenReward = 0,
}) => {
  const [revealStep, setRevealStep] = useState<RevealStep>('initial')
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  const [isProcessing, setIsProcessing] = useState(false)

  const fragment = revealedItems.find((item) => item.type === 'Fragment')
  const newCard = revealedItems.find((item) => item.type === 'UserCard')
  const newUnrevealedCard = revealedItems.find(
    (item) => item.type === 'Unrevealed'
  )

  useEffect(() => {
    let interval: NodeJS.Timeout

    if (revealStep === 'loading') {
      interval = setInterval(() => {
        setLoadingMessageIndex((prev) =>
          prev < loadingMessages.length - 1 ? prev + 1 : prev
        )
      }, 1000)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [revealStep])

  useEffect(() => {
    if (revealedItems.length > 0 && revealStep === 'loading') {
      setRevealStep('fragment')
    }
  }, [revealedItems, revealStep])

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRevealStep('initial')
      setLoadingMessageIndex(0)
      setIsProcessing(false)
    }
  }, [isOpen])

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    // Only allow closing if not currently processing
    if (!isProcessing) {
      onClose()
    }
  }

  const handleCardClick = async (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent click from reaching overlay

    // Don't process clicks if already processing
    if (isProcessing) return

    if (revealStep === 'initial') {
      setIsProcessing(true)
      setRevealStep('loading')
      setLoadingMessageIndex(0)
      try {
        await onReveal()
      } catch (error) {
        console.error('Error during reveal:', error)
        onClose()
      }
      setIsProcessing(false)
    } else if (revealStep === 'fragment') {
      setRevealStep('newCard')
    } else if (revealStep === 'newCard' && newUnrevealedCard) {
      setRevealStep('newUnrevealedCard')
    } else if (revealStep === 'newCard' && !newUnrevealedCard) {
      setRevealStep('tokens')
    } else if (revealStep === 'newUnrevealedCard') {
      setRevealStep('tokens')
    } else if (revealStep === 'tokens') {
      onClose()
    }
  }

  const renderContent = () => {
    switch (revealStep) {
      case 'initial':
        return (
          <motion.div
            className="reveal-modal-card"
            variants={cardVariants}
            initial="initial"
            animate={isRevealing ? 'flipped' : 'initial'}
            exit="exit"
            onClick={handleCardClick}
          >
            <div className="card-front">
              <img src={initialCardUrl} alt="Card" />
            </div>
            <div className="reveal-instruction">Tap to reveal!</div>
          </motion.div>
        )

      case 'loading':
        return (
          <motion.div
            className="reveal-content loading-reveal"
            variants={contentVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <div className="magical-circle">
              <div className="circle-inner"></div>
              <div className="circle-outer"></div>
            </div>
            <motion.p
              key={loadingMessageIndex}
              variants={textVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="loading-message"
            >
              {loadingMessages[loadingMessageIndex]}
            </motion.p>
          </motion.div>
        )

      case 'fragment':
        return (
          fragment && (
            <motion.div
              className="reveal-content fragment-reveal"
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              onClick={handleCardClick}
            >
              <motion.img
                src={fragment.url}
                alt={fragment.name}
                variants={cardContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              />
              <motion.div
                className="reveal-text"
                variants={textVariants}
                initial="hidden"
                animate="visible"
              >
                <h2>{fragment.name}</h2>
                <p>
                  <span
                    className={`reveal-rarity-badge reveal-rarity-${fragment.rarity?.toLowerCase()}`}
                  >
                    {fragment.rarity}
                  </span>
                  Fragment collected!
                </p>
                <span className="tap-instruction">Tap to continue</span>
              </motion.div>
            </motion.div>
          )
        )

      case 'newCard':
        return (
          newCard && (
            <motion.div
              className="reveal-content new-card-reveal"
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              onClick={handleCardClick}
            >
              <motion.img
                src={newCard.url}
                alt={newCard.name}
                variants={cardContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              />
              <motion.div
                className="reveal-text"
                variants={textVariants}
                initial="hidden"
                animate="visible"
              >
                <h2>{newCard.name}</h2>
                <p>
                  <span
                    className={`reveal-rarity-badge reveal-rarity-${newCard.rarity?.toLowerCase()}`}
                  >
                    {newCard.rarity}
                  </span>
                  Revealed {newCard.timesOpened} times
                </p>
                <span className="tap-instruction">Tap to continue</span>
              </motion.div>
            </motion.div>
          )
        )

      case 'newUnrevealedCard':
        return (
          newUnrevealedCard && (
            <motion.div
              className="reveal-content new-card-reveal"
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              onClick={handleCardClick}
            >
              <motion.img
                src={newUnrevealedCard.url}
                alt="New card"
                variants={cardContentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              />
              <motion.div
                className="reveal-text"
                variants={textVariants}
                initial="hidden"
                animate="visible"
              >
                <h2>
                  New{' '}
                  <span
                    className={`reveal-rarity-badge reveal-rarity-${newUnrevealedCard.rarity?.toLowerCase()}`}
                  >
                    {newUnrevealedCard.rarity}
                  </span>{' '}
                  Unrevealed Card!
                </h2>
                <span className="tap-instruction">Tap to continue</span>
              </motion.div>
            </motion.div>
          )
        )

      case 'tokens':
        return (
          <motion.div
            className="reveal-content tokens-reveal"
            variants={contentVariants}
            initial="enter"
            animate="center"
            exit="exit"
            onClick={handleCardClick}
          >
            <motion.div
              className="token-animation"
              variants={cardContentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className="token-amount">+{tokenReward}</div>
              <div className="token-label">CARD tokens</div>
            </motion.div>
            <motion.span
              className="tap-instruction"
              variants={textVariants}
              initial="hidden"
              animate="visible"
            >
              Tap to close
            </motion.span>
          </motion.div>
        )
    }
  }

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="reveal-modal-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={handleOverlayClick}
        >
          {renderContent()}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
